import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import {
  Button,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import axiosInstance from '@/setup/axios/axiosInstance';
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { AppPagination } from '@/__core/components';
import IssueExportPreviewModal, { SelectedItemPreviewRow } from './modals/IssueExportPreviewModal';
import { createIssue, getNeedExportIssues } from '../redux/IssueCRUD';
import { getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { MessageSnackbar } from '@/__core/helpers/components';

import { InventoryIssueRow } from '../types';

export type IssueNeedExportListHandle = {
  requestRefresh: (options?: { showSuccessMessage?: boolean }) => void;
};

interface IssueNeedExportListProps {
  companyId?: string;
  searchQuery?: string;
  status?: string;
  onSelectRow: (row: InventoryIssueRow) => void;
  onFetchError?: (message: string) => void;
  onRefreshSuccess?: () => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const resolveListCompanyId = (companyId?: string) => {
  const resolved = (companyId || getCurrentCompanyId() || '').trim();
  if (!resolved || resolved === 'all' || resolved === 'global' || !UUID_PATTERN.test(resolved)) {
    return undefined;
  }
  return resolved;
};

interface StockBalanceSnapshot {
  on_hand_qty: number;
  allocated_qty: number;
  available_qty: number;
}

interface MaterialRowRef {
  key: string;
  issueId: string;
  lineId: string;
  itemId: string;
  itemCode: string;
  uomId: string;
  companyId: string;
  warehouseId: string;
}

interface SelectedCreateLine {
  lineKey: string;
  itemId: string;
  uomId?: string;
  sourceRequestLineId?: string;
  issueId: string;
  companyId: string;
  warehouseId: string;
  exportQty: number;
  itemCode: string;
  itemName: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const toUuidOrUndefined = (value: unknown): string | undefined => {
  const normalized = String(value || '').trim();
  return UUID_REGEX.test(normalized) ? normalized : undefined;
};

const resolveStatusName = (status: unknown): string => {
  if (!status) return '';
  if (typeof status === 'string') return status;
  if (typeof status === 'object' && status !== null) {
    const obj = status as Record<string, unknown>;
    return String(obj.name || obj.code || '');
  }
  return '';
};

const statusLabel = (status: unknown) => {
  const name = resolveStatusName(status);
  switch (name.toLowerCase()) {
    case 'posted': return 'Da ghi so';
    case 'ready_to_post':
    case 'ready': return 'San sang ghi so';
    case 'picked': return 'Da pick';
    case 'inspected': return 'Da kiem';
    case 'draft': return 'Nhap';
    case 'approved': return 'Da duyet';
    case 'cancelled':
    case 'canceled': return 'Da huy';
    default: return name || '-';
  }
};

const statusColorProps = (status: unknown): { fg: string } => {
  if (status && typeof status === 'object' && status !== null) {
    const obj = status as Record<string, unknown>;
    if (obj.color) {
      return { fg: String(obj.color) };
    }
  }

  const name = resolveStatusName(status);
  switch (name.toLowerCase()) {
    case 'posted': return { fg: '#166534' };
    case 'ready_to_post':
    case 'ready': return { fg: '#9a3412' };
    case 'picked':
    case 'inspected': return { fg: '#075985' };
    case 'cancelled':
    case 'canceled': return { fg: '#991b1b' };
    case 'draft': return { fg: '#854d0e' };
    case 'approved': return { fg: '#1e40af' };
    default: return { fg: '#374151' };
  }
};

const formatQty = (value: number) => Number(value || 0).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });



const unwrapStockBalanceRecord = (data: any): any => {
  if (Array.isArray(data)) {
    return data[0] || null;
  }

  if (Array.isArray(data?.results)) {
    return data.results[0] || null;
  }

  if (data && typeof data === 'object' && ('on_hand_qty' in data || 'allocated_qty' in data || 'available_qty' in data)) {
    return data;
  }

  return null;
};

const IssueNeedExportList = forwardRef<IssueNeedExportListHandle, IssueNeedExportListProps>(function IssueNeedExportList(
  { companyId, searchQuery = '', status = 'all', onSelectRow, onFetchError, onRefreshSuccess },
  ref
) {
  const [rows, setRows] = useState<InventoryIssueRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [showSuccessOnLoad, setShowSuccessOnLoad] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [printError, setPrintError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<Set<string>>(new Set());
  const [stockByLineKey, setStockByLineKey] = useState<Record<string, StockBalanceSnapshot | null>>({});
  const [exportedByLineKey, setExportedByLineKey] = useState<Record<string, number>>({});
  const [selectedByLineKey, setSelectedByLineKey] = useState<Record<string, boolean>>({});
  const [allocationByLineKey, setAllocationByLineKey] = useState<Record<string, string>>({});
  const [inProgressByLineKey, setInProgressByLineKey] = useState<Record<string, number>>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const createInFlightRef = useRef(false);
  const onFetchErrorRef = useRef(onFetchError);
  const onRefreshSuccessRef = useRef(onRefreshSuccess);
  onFetchErrorRef.current = onFetchError;
  onRefreshSuccessRef.current = onRefreshSuccess;

  const resolvedCompanyId = useMemo(() => resolveListCompanyId(companyId), [companyId]);

  useEffect(() => {
    let active = true;

    const fetchRows = async () => {
      setListLoading(true);
      try {
        const response = await getNeedExportIssues({
          companyId: resolvedCompanyId,
          search: searchQuery || undefined,
          status: status !== 'all' ? status : undefined,
          page: 1,
          pageSize: DEFAULT_PAGE_SIZE,
        });

        if (!active) return;

        setRows(response.results || []);

        const seededStock: Record<string, StockBalanceSnapshot | null> = {};
        (response.results || []).forEach((issueRow) => {
          (issueRow.lines || []).forEach((line) => {
            const lineKey = `${issueRow.id}__${line.id}`;
            const raw = line.raw || {};
            if (raw.on_hand_qty !== undefined || raw.allocated_qty !== undefined || raw.available_qty !== undefined) {
              seededStock[lineKey] = {
                on_hand_qty: Number(raw.on_hand_qty || 0),
                allocated_qty: Number(raw.allocated_qty || 0),
                available_qty: Number(raw.available_qty || 0),
              };
            }
          });
        });
        if (Object.keys(seededStock).length > 0) {
          setStockByLineKey(seededStock);
        }

        if (showSuccessOnLoad) {
          onRefreshSuccessRef.current?.();
          setShowSuccessOnLoad(false);
        }
      } catch (error: unknown) {
        if (!active) return;
        setRows([]);
        setShowSuccessOnLoad(false);
        const message =
          error && typeof error === 'object' && 'message' in error && typeof (error as { message: unknown }).message === 'string'
            ? (error as { message: string }).message
            : 'Không thể tải danh sách phiếu xuất kho.';
        onFetchErrorRef.current?.(message);
      } finally {
        if (active) {
          setListLoading(false);
        }
      }
    };

    const timeout = window.setTimeout(() => {
      void fetchRows();
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, [resolvedCompanyId, searchQuery, showSuccessOnLoad, status, reloadTick]);

  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: (options) => {
        if (options?.showSuccessMessage) {
          setShowSuccessOnLoad(true);
        }
        setReloadTick((current) => current + 1);
      },
    }),
    []
  );

  const materialRows = useMemo<MaterialRowRef[]>(() => {
    return rows.flatMap((issueRow) => {
      const issueLines = issueRow.lines || [];
      return issueLines
        .filter((line) => (line.item_code || '').trim() && (line.item_id || '').trim())
        .map((line) => ({
          key: `${issueRow.id}__${line.id}`,
          issueId: issueRow.id,
          lineId: line.id,
          itemId: line.item_id || '',
          itemCode: (line.item_code || '').trim(),
          uomId: line.uom_id || '',
          companyId: issueRow.company_id || '',
          warehouseId: issueRow.warehouse_id || '',
        }));
    });
  }, [rows]);

  const stockRequestKey = useMemo(() => materialRows.map((row) => `${row.itemCode}|${row.uomId}|${row.companyId}|${row.warehouseId}`).join('||'), [materialRows]);
  const transactionRequestKey = useMemo(() => materialRows.map((row) => `${row.itemId}|${row.companyId}|${row.warehouseId}`).join('||'), [materialRows]);

  useEffect(() => {
    if (!materialRows.length) {
      setStockByLineKey({});
      return;
    }

    let cancelled = false;

    void (async () => {
      const uniqueRequests = Array.from(new Map(
        materialRows
          .filter((row) => row.itemCode && row.companyId && row.warehouseId)
          .map((row) => [`${row.itemCode}__${row.uomId}__${row.companyId}__${row.warehouseId}`, row]),
      ).values());

      const responseByRequestKey = new Map<string, StockBalanceSnapshot | null>();

      await Promise.all(uniqueRequests.map(async (request) => {
        const requestKey = `${request.itemCode}__${request.uomId}__${request.companyId}__${request.warehouseId}`;

        try {
          const response = await axiosInstance.get('/inventory/stock-balance/', {
            params: {
              item_code: request.itemCode,
              company_id: request.companyId,
              store_id: request.warehouseId,
              ...(request.uomId ? { uom_id: request.uomId } : {}),
            },
          });

          const rawRecord = unwrapStockBalanceRecord(response.data);
          responseByRequestKey.set(requestKey, rawRecord
            ? {
              on_hand_qty: Number(rawRecord.on_hand_qty) || 0,
              allocated_qty: Number(rawRecord.allocated_qty) || 0,
              available_qty: Number(rawRecord.available_qty) || 0,
            }
            : {
              on_hand_qty: 0,
              allocated_qty: 0,
              available_qty: 0,
            });
        } catch (_error) {
          responseByRequestKey.set(requestKey, null);
        }
      }));

      if (cancelled) {
        return;
      }

      setStockByLineKey((current) => {
        const next = { ...current };
        materialRows.forEach((row) => {
          const requestKey = `${row.itemCode}__${row.uomId}__${row.companyId}__${row.warehouseId}`;
          const apiResult = responseByRequestKey.get(requestKey);
          if (apiResult != null && current[row.key] == null) {
            next[row.key] = apiResult;
          }
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [materialRows, stockRequestKey]);

  useEffect(() => {
    if (!materialRows.length) {
      setExportedByLineKey({});
      return;
    }

    let cancelled = false;

    void (async () => {
      const uniqueRequests = Array.from(new Map(
        materialRows
          .filter((row) => row.itemId && row.companyId && row.warehouseId)
          .map((row) => [`${row.itemId}__${row.companyId}__${row.warehouseId}`, row]),
      ).values());

      const exportedByRequestKey = new Map<string, number>();

      await Promise.all(uniqueRequests.map(async (request) => {
        const requestKey = `${request.itemId}__${request.companyId}__${request.warehouseId}`;
        let exportedQty = 0;

        try {
          const response = await axiosInstance.get('/inventory/stock-transactions/', {
            params: {
              company: request.companyId,
              store_id: request.warehouseId,
              item_id: request.itemId,
              txn_type: 'issue',
            },
          });

          const data = response.data;
          const rowsData = Array.isArray(data)
            ? data
            : (Array.isArray(data?.results) ? data.results : []);

          rowsData.forEach((transaction: any) => {
            const delta = Number(transaction?.quantity_delta || 0);
            if (delta < 0) {
              exportedQty += Math.abs(delta);
            } else {
              exportedQty += delta;
            }
          });

          exportedByRequestKey.set(requestKey, exportedQty);
        } catch (_error) {
          exportedByRequestKey.set(requestKey, 0);
        }
      }));

      if (cancelled) {
        return;
      }

      const nextByLineKey: Record<string, number> = {};
      materialRows.forEach((row) => {
        const requestKey = `${row.itemId}__${row.companyId}__${row.warehouseId}`;
        nextByLineKey[row.key] = Number(exportedByRequestKey.get(requestKey) || 0);
      });
      setExportedByLineKey(nextByLineKey);
    })();

    return () => {
      cancelled = true;
    };
  }, [materialRows, transactionRequestKey]);

  const groupedRows = useMemo(() => {
    const groups = new Map<string, InventoryIssueRow[]>();

    rows.forEach((row) => {
      const key = row.order_no || 'Khong co ma don hang';
      const current = groups.get(key) || [];
      current.push(row);
      groups.set(key, current);
    });

    return Array.from(groups.entries()).map(([orderNo, issueRows]) => ({ orderNo, issueRows }));
  }, [rows]);

  useEffect(() => {
    const availableKeys = new Set(groupedRows.map((group) => group.orderNo));
    setSelectedGroupKeys((prev) => new Set(Array.from(prev).filter((key) => availableKeys.has(key))));
  }, [groupedRows]);

  const selectedGroups = useMemo(
    () => {
      const keysFromLineSelection = new Set<string>()
      groupedRows.forEach((group) => {
        const hasSelectedLine = group.issueRows.some((issueRow) =>
          (issueRow.lines || []).some((line) => selectedByLineKey[`${issueRow.id}__${line.id}`])
        )
        if (hasSelectedLine) {
          keysFromLineSelection.add(group.orderNo)
        }
      })

      const effectiveKeys = new Set<string>([...Array.from(selectedGroupKeys), ...Array.from(keysFromLineSelection)])
      return groupedRows.filter((group) => effectiveKeys.has(group.orderNo))
    },
    [groupedRows, selectedGroupKeys, selectedByLineKey]
  );

  const selectedIssueRowsForAction = useMemo(
    () => selectedGroups.flatMap((group) => group.issueRows),
    [selectedGroups]
  );

  const nonSelectableLineKeys = useMemo(() => {
    const blockedKeys = new Set<string>();

    rows.forEach((issueRow) => {
      (issueRow.lines || []).forEach((line) => {
        const lineKey = `${issueRow.id}__${line.id}`;
        const stock = stockByLineKey[lineKey];
        const requestedQty = Number(line.issued_qty || 0);
        const exportedQty = Number(exportedByLineKey[lineKey] || 0);
        const inProgressQty = Number(line.in_progress_qty || 0) + Number(inProgressByLineKey[lineKey] || 0);
        const remainingQty = requestedQty - exportedQty - inProgressQty;
        const onHandQty = Number(stock?.on_hand_qty || 0);
        const allocatedForOtherQty = Number(stock?.allocated_qty || 0);

        if (remainingQty > (onHandQty - allocatedForOtherQty)) {
          blockedKeys.add(lineKey);
        }
      });
    });

    return blockedKeys;
  }, [rows, stockByLineKey, inProgressByLineKey, exportedByLineKey]);

  useEffect(() => {
    const validLineKeys = new Set(materialRows.map((row) => row.key));

    setSelectedByLineKey((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => validLineKeys.has(key) && !nonSelectableLineKeys.has(key)),
    ));

    setAllocationByLineKey((current) => Object.fromEntries(
      Object.entries(current).filter(([key]) => validLineKeys.has(key)),
    ));
  }, [materialRows, nonSelectableLineKeys]);

  const selectedItemCount = useMemo(() => Object.values(selectedByLineKey).filter(Boolean).length, [selectedByLineKey]);

  const selectedPreviewItems = useMemo<SelectedItemPreviewRow[]>(() => {
    const result: SelectedItemPreviewRow[] = [];

    rows.forEach((issueRow) => {
      (issueRow.lines || []).forEach((line) => {
        const lineKey = `${issueRow.id}__${line.id}`;
        if (!selectedByLineKey[lineKey]) {
          return;
        }

        const stock = stockByLineKey[lineKey];
        const requestedQty = Number(line.issued_qty || 0);
        const exportedQty = Number(exportedByLineKey[lineKey] || 0);
        const inProgressQty = Number(line.in_progress_qty || 0) + Number(inProgressByLineKey[lineKey] || 0);
        const availableQty = Number(stock?.available_qty || 0);
        const remainingQty = requestedQty - exportedQty - inProgressQty;
        const defaultExportQty = remainingQty > 0
          ? Math.min(remainingQty, Math.max(0, availableQty))
          : 0;

        const parsedExportQty = Number(allocationByLineKey[lineKey]);
        const exportQty = Number.isFinite(parsedExportQty) ? parsedExportQty : defaultExportQty;

        result.push({
          key: lineKey,
          itemCode: line.item_code || '-',
          itemName: line.item_name || '-',
          exportQty: exportQty > 0 ? exportQty : 0,
        });
      });
    });

    return result;
  }, [rows, selectedByLineKey, stockByLineKey, allocationByLineKey, inProgressByLineKey, exportedByLineKey]);

  const selectedCreateLines = useMemo<SelectedCreateLine[]>(() => {
    const result: SelectedCreateLine[] = [];

    rows.forEach((issueRow) => {
      (issueRow.lines || []).forEach((line) => {
        const lineKey = `${issueRow.id}__${line.id}`;
        if (!selectedByLineKey[lineKey]) {
          return;
        }

        const stock = stockByLineKey[lineKey];
        const requestedQty = Number(line.issued_qty || 0);
        const exportedQty = Number(exportedByLineKey[lineKey] || 0);
        const inProgressQty = Number(line.in_progress_qty || 0) + Number(inProgressByLineKey[lineKey] || 0);
        const availableQty = Number(stock?.available_qty || 0);
        const remainingQty = requestedQty - exportedQty - inProgressQty;
        const defaultExportQty = remainingQty > 0
          ? Math.min(remainingQty, Math.max(0, availableQty))
          : 0;

        const parsedExportQty = Number(allocationByLineKey[lineKey]);
        const exportQty = Number.isFinite(parsedExportQty) ? parsedExportQty : defaultExportQty;

        if (!line.item_id || !issueRow.company_id || !issueRow.warehouse_id || exportQty <= 0) {
          return;
        }

        result.push({
          lineKey,
          itemId: line.item_id,
          uomId: line.uom_id,
          sourceRequestLineId: toUuidOrUndefined((line as any)?.raw?.source_request_line_id)
            || toUuidOrUndefined((line as any)?.raw?.source_request_line)
            || toUuidOrUndefined(line.id),
          issueId: issueRow.id,
          companyId: issueRow.company_id,
          warehouseId: issueRow.warehouse_id,
          exportQty,
          itemCode: line.item_code || '-',
          itemName: line.item_name || '-',
        });
      });
    });

    return result;
  }, [rows, selectedByLineKey, stockByLineKey, inProgressByLineKey, allocationByLineKey, exportedByLineKey]);

  const totalCount = groupedRows.length;
  const totalPages = Math.max(Math.ceil(totalCount / pageSize), 1);
  const normalizedPage = Math.min(page, totalPages);
  const pagedGroupedRows = useMemo(() => {
    const startIndex = (normalizedPage - 1) * pageSize;
    return groupedRows.slice(startIndex, startIndex + pageSize);
  }, [groupedRows, normalizedPage, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const tableRows = useMemo(() => {
    const flat: any[] = [];
    pagedGroupedRows.forEach((group) => {
      const lineRows = group.issueRows.flatMap((issueRow: InventoryIssueRow) => {
        const issueLines = issueRow.lines || [];
        if (!issueLines.length) {
          return [{
            lineKey: '',
            issueRow,
            line: null,
            itemCode: '-',
            itemName: '-',
            issuedQty: 0,
            uomName: '-',
            stockOnHand: 0,
            stockAllocated: 0,
            availableQty: 0,
            exportedQty: 0,
            inProgressQty: 0,
            remainingQty: 0,
            isSelectable: false,
            shouldHighlight: false,
          }];
        }
        return issueLines.map((line: any) => {
          const lineKey = `${issueRow.id}__${line.id}`;
          const stock = stockByLineKey[lineKey] || null;
          const requestedQty = Number(line.issued_qty || 0);
          const onHandQty = Number(stock?.on_hand_qty || 0);
          const allocatedForOtherQty = Number(stock?.allocated_qty || 0);
          const exportedQty = Number(exportedByLineKey[lineKey] || 0);
          const inProgressQty = Number(line.in_progress_qty || 0) + Number(inProgressByLineKey[lineKey] || 0);
          const availableQty = Number(stock?.available_qty || 0);
          const remainingQty = requestedQty - exportedQty - inProgressQty;
          const shouldHighlight = remainingQty > (onHandQty - allocatedForOtherQty);
          const isSelectable = !shouldHighlight;
          return {
            lineKey,
            issueRow,
            line,
            itemCode: line.item_code || '-',
            itemName: line.item_name || '-',
            issuedQty: requestedQty,
            uomName: line.uom_name || '-',
            stockOnHand: onHandQty,
            stockAllocated: allocatedForOtherQty,
            availableQty,
            exportedQty,
            inProgressQty,
            remainingQty,
            isSelectable,
            shouldHighlight,
          };
        });
      });

      flat.push({
        id: `group-${group.orderNo}`,
        __groupRow: true,
        orderNo: group.orderNo,
        issueRows: group.issueRows,
      });

      lineRows.forEach((lr: any) => flat.push(lr));
    });
    return flat;
  }, [pagedGroupedRows, stockByLineKey, inProgressByLineKey, exportedByLineKey]);

  const selectedLineKeysSet = useMemo(() => new Set(Object.keys(selectedByLineKey).filter((k) => selectedByLineKey[k])), [selectedByLineKey]);

  const selectableLineKeys = useMemo(() => {
    const lines = tableRows.filter((r: any) => !r.__groupRow && r.lineKey);
    return lines.map((r: any) => r.lineKey);
  }, [tableRows]);

  const handleSelectAll = () => {
    const allSelected = selectableLineKeys.every((k) => selectedByLineKey[k]);
    if (allSelected) {
      setSelectedByLineKey((prev) => {
        const next = { ...prev };
        selectableLineKeys.forEach((k) => { delete next[k]; });
        return next;
      });
    } else {
      setSelectedByLineKey((prev) => {
        const next = { ...prev };
        selectableLineKeys.forEach((k) => { next[k] = true; });
        return next;
      });
    }
  };

  const handleRowSelect = (id: string) => {
    if (id.startsWith('group-')) {
      const groupOrderNo = id.slice('group-'.length);
      setSelectedGroupKeys((prev) => {
        const next = new Set(prev);
        if (next.has(groupOrderNo)) {
          next.delete(groupOrderNo);
        } else {
          next.add(groupOrderNo);
        }
        return next;
      });
      return;
    }

    setSelectedByLineKey((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: true };
    });
  };

  const isRowSelected = (row: any) => row.__groupRow ? selectedGroupKeys.has(row.orderNo) : !!selectedByLineKey[row.lineKey];

  const areAllRowsSelected = selectableLineKeys.length > 0 && selectableLineKeys.every((k) => selectedByLineKey[k]);

  const areSomeRowsSelected = selectableLineKeys.some((k) => selectedByLineKey[k]) && !areAllRowsSelected;

  const getRowId = (row: any) => row.__groupRow ? (row.id || '') : row.lineKey || '';

  const getRowClassName = (row: any) => {
    if (row.__groupRow) return 'group-row';
    if (row.shouldHighlight) return 'blocked-row';
    return '';
  };

  const isCheckboxDisabledForRow = (row: any) => row.__groupRow ? false : !row.isSelectable;

  const columns = useMemo(() => [
    {
      id: 'item_code',
      label: 'Mã vật tư',
      width: '140px',
      render: (_: unknown, row: any) => row.__groupRow ? (
        <Typography sx={{ fontWeight: 700, color: '#3730a3' }}>
          {row.orderNo}
        </Typography>
      ) : row.itemCode,
    },
    { id: 'item_name', label: 'Tên vật tư', width: '220px', render: (_: unknown, row: any) => row.__groupRow ? null : row.itemName },
    { id: 'issued_qty', label: 'Số lượng yêu cầu', width: '140px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.issuedQty) },
    { id: 'uom', label: 'ĐVT', width: '100px', render: (_: unknown, row: any) => row.__groupRow ? null : row.uomName },
    { id: 'stock_on_hand', label: 'Tồn kho', width: '120px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.stockOnHand) },
    { id: 'stock_allocated', label: 'Đã gán cho ĐH khác', width: '170px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.stockAllocated) },
    { id: 'available_qty', label: 'Số lượng khả dụng', width: '160px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.availableQty) },
    { id: 'exported_qty', label: 'Đã xuất', width: '120px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.exportedQty) },
    { id: 'in_progress_qty', label: 'Đang xuất', width: '120px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.inProgressQty) },
    { id: 'remaining_qty', label: 'Còn thiếu', width: '130px', align: 'right' as const, render: (_: unknown, row: any) => row.__groupRow ? null : formatQty(row.remainingQty) },
    {
      id: 'allocation',
      label: 'Số lượng xuất',
      width: '140px',
      render: (_: unknown, row: any) => {
        if (row.__groupRow) return null;
        const lineKey = row.lineKey;
        const val = allocationByLineKey[lineKey] ?? String(Math.min(row.remainingQty, Math.max(0, row.availableQty)));
        return (
          <TextField
            size='small'
            type='number'
            value={val}
            onClick={(e: any) => e.stopPropagation()}
            onChange={(e: any) => setAllocationByLineKey((prev) => ({ ...prev, [lineKey]: e.target.value }))}
            inputProps={{ min: 0, step: '0.001' }}
            sx={{ minWidth: 110 }}
          />
        );
      },
    },
    {
      id: 'status',
      label: 'Trạng thái',
      width: '140px',
      render: (_: unknown, row: any) => {
        if (row.__groupRow) return null;
        return (
          <Typography sx={{ fontWeight: 600, color: statusColorProps(row.issueRow.status).fg }}>
            {statusLabel(row.issueRow.status)}
          </Typography>
        );
      },
    },
    { id: 'store_name', label: 'Kho xuất', width: '180px', render: (_: unknown, row: any) => row.__groupRow ? null : (row.issueRow.store_name || '-') },
  ], [selectedGroups, allocationByLineKey, stockByLineKey, inProgressByLineKey, exportedByLineKey]);

  const handleConfirmCreateIssue = async ({ picker, exportDate, note }: { picker: { value: string; label: string }; exportDate: Date; note?: string }) => {
    if (createInFlightRef.current) {
      return;
    }

    if (!selectedCreateLines.length) {
      setCreateError('Không có dòng hợp lệ để tạo phiếu xuất.');
      return;
    }

    const first = selectedCreateLines[0];
    const mixedCompany = selectedCreateLines.some((line) => line.companyId !== first.companyId);
    const mixedWarehouse = selectedCreateLines.some((line) => line.warehouseId !== first.warehouseId);
    if (mixedCompany || mixedWarehouse) {
      setCreateError('Chỉ hỗ trợ tạo phiếu cho các dòng cùng công ty và cùng kho xuất.');
      return;
    }

    const issuedAt = `${format(exportDate, 'yyyy-MM-dd')}T00:00:00`;

    createInFlightRef.current = true;
    setCreateLoading(true);
    setCreateError(null);

    try {
      const created = await createIssue({
        company: first.companyId,
        warehouse: first.warehouseId,
        issued_at: issuedAt,
        picker: picker.value,
        ...(note?.trim() ? { note: note.trim() } : {}),
        lines: selectedCreateLines.map((line) => ({
          item: line.itemId,
          ...(line.uomId ? { uom: line.uomId } : {}),
          ...(line.sourceRequestLineId ? { source_request_line: line.sourceRequestLineId } : {}),
          issued_qty: line.exportQty,
        })),
      });

      if (!created?.id) {
        throw new Error('Create issue failed.');
      }

      const createdLines = Array.isArray(created?.lines) ? created.lines : [];

      setInProgressByLineKey((current) => {
        const next = { ...current };
        selectedCreateLines.forEach((line, index) => {
          const createdQty = Number(createdLines[index]?.issued_qty);
          const qtyToApply = Number.isFinite(createdQty) && createdQty > 0
            ? createdQty
            : Number(line.exportQty || 0);

          next[line.lineKey] = Number(next[line.lineKey] || 0) + qtyToApply;
        });
        return next;
      });

      setSelectedByLineKey((current) => {
        const next = { ...current };
        selectedCreateLines.forEach((line) => {
          delete next[line.lineKey];
        });
        return next;
      });

      setPreviewOpen(false);
      setCreateError(null);
    } catch (error: any) {
      const backendDetail = error?.response?.data?.detail;
      setCreateError(typeof backendDetail === 'string' ? backendDetail : 'Tạo phiếu xuất thất bại. Vui lòng kiểm tra dữ liệu và thử lại.');
    } finally {
      setCreateLoading(false);
      createInFlightRef.current = false;
    }
  };

  if (listLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!groupedRows.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center', color: '#6b7280' }}>
        Không có dữ liệu cần xuất.
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <DataTableModule
        data={tableRows}
        columns={columns as any}
        leftFilterWidth={0}
        tableLayout='auto'
        tableSx={{
          '& th, & td': {
            px: 1,
          },
        }}
        checkboxColumnWidth={56}
        checkboxCellPaddingX={0.5}
        showCheckbox
        stickyCheckbox
        checkboxDisabled={selectableLineKeys.length === 0}
        isCheckboxDisabledForRow={isCheckboxDisabledForRow}
        hideCheckboxForRow={(row: any) => !!row.__hideCheckbox}
        onRowSelect={handleRowSelect}
        onRowClick={(row: any) => { if (!row.__groupRow) onSelectRow(row.issueRow); }}
        onSelectAll={handleSelectAll}
        getRowId={getRowId}
        isRowSelected={isRowSelected}
        areAllRowsSelected={areAllRowsSelected}
        areSomeRowsSelected={areSomeRowsSelected}
        getRowClassName={getRowClassName}
      />

      {(selectedGroupKeys.size > 0 || selectedGroups.length > 0 || selectedItemCount > 0) && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 20,
            mt: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            p: 1.5,
            border: '1px solid #e5e7eb',
            borderRadius: 1,
            backgroundColor: '#ffffff',
            boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
            boxSizing: 'border-box',
            width: '100%',
          }}
        >
          <Typography variant='body2' sx={{ alignSelf: 'center', mr: 'auto' }}>
            Đã chọn {selectedGroups.length} nhóm, {selectedIssueRowsForAction.length} phiếu
          </Typography>
          {selectedItemCount > 0 && (
            <Button variant='contained' onClick={() => setPreviewOpen(true)} sx={{ textTransform: 'none' }}>
              Tạo phiếu xuất kho
            </Button>
          )}
        </Box>
      )}

      <AppPagination
        count={totalCount}
        page={normalizedPage}
        pageSize={pageSize}
        totalPages={totalPages}
        itemLabel='nhóm xuất'
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setPage(1);
        }}
      />

      

      <IssueExportPreviewModal
        open={previewOpen}
        onClose={() => {
          if (createLoading) {
            return;
          }
          setPreviewOpen(false);
          setCreateError(null);
        }}
        items={selectedPreviewItems}
        onConfirm={handleConfirmCreateIssue}
        confirmLoading={createLoading}
        confirmError={createError}
      />

      <MessageSnackbar
        open={!!printError}
        message={printError}
        severity='error'
        onClose={() => setPrintError(null)}
      />
      <MessageSnackbar
        open={!!actionSuccess}
        message={actionSuccess}
        severity='success'
        onClose={() => setActionSuccess(null)}
      />
    </Box>
  );
});

export default IssueNeedExportList;
